/*
  Quick Add drawer
  - Opens from the bag button on product cards ([data-quick-add])
  - Bottom sheet on mobile / centered popup on desktop (CSS-driven)
  - Adds to cart through the theme's cart-container (live cart drawer)
*/

class QuickAddDrawer extends HTMLElement {
  constructor () {
    super()

    this.backdrop = this.querySelector('.qa-backdrop')
    this.panel = this.querySelector('.qa-panel')
    this.atcBtn = this.querySelector('[data-qa-atc]')
    this.wishlistBtn = this.querySelector('[data-qa-wishlist]')
    this.optionsWrapper = this.querySelector('[data-qa-options]')
    this.gallery = this.querySelector('[data-qa-gallery]')
    this.dots = this.querySelector('[data-qa-dots]')
    this.thumbs = this.querySelector('[data-qa-thumbs]')

    this.product = null
    this.selectedOptions = {}

    this.querySelectorAll('[data-qa-close]').forEach(el =>
      el.addEventListener('click', () => this.close())
    )

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.isOpen) this.close()
    })

    this.atcBtn.addEventListener('click', () => this.onAddToCart())

    this.wishlistBtn?.addEventListener('click', () => this.onToggleWishlist())

    this.gallery.addEventListener('scroll', () => this.onGalleryScroll(), { passive: true })

    // Open the drawer when a card's bag button is clicked (works for
    // AJAX-loaded cards too since the listener is delegated)
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-quick-add]')
      if (!trigger) return

      event.preventDefault()
      event.stopPropagation()

      const card = trigger.closest('product-card')
      const json = card?.querySelector('[data-quick-add-json]')
      if (!json) return

      let data
      try {
        data = JSON.parse(json.textContent)
      } catch (e) {
        console.error('Quick add: invalid product JSON', e)
        return
      }

      // Single-variant products skip the drawer and go straight to the cart
      if (data.has_only_default_variant && data.variants.length === 1) {
        this.directAdd(trigger, data.variants[0])
      } else {
        this.open(data)
      }
    })
  }

  get isOpen () {
    return !this.panel.hidden
  }

  get cartContainer () {
    return document.querySelector('cart-container')
  }

  formatMoney (cents) {
    try {
      return window.Shopify.formatMoney(cents)
    } catch (e) {
      return (cents / 100).toFixed(0)
    }
  }

  async directAdd (trigger, variant) {
    if (!variant || !variant.available) return

    trigger.classList.add('loading')
    trigger.disabled = true

    await this.addVariantToCart(variant.id)

    trigger.classList.remove('loading')
    trigger.disabled = false
  }

  async addVariantToCart (variantId) {
    const cart = this.cartContainer

    if (cart) {
      await cart.add({ items: [{ id: variantId, quantity: 1 }] })
    } else {
      // Fallback if the cart drawer is not on the page
      await fetch(`${window.Shopify.routes.cart_add_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
      })
      window.location.href = window.Shopify.routes.cart_url
    }
  }

  open (product) {
    this.product = product
    this.selectedOptions = {}

    this.renderHeader()
    this.renderGallery()
    this.renderInfo()
    this.renderOptions()
    this.renderSizeGuide()
    this.renderWishlist()
    this.updateAtcState()

    this.backdrop.hidden = false
    this.panel.hidden = false

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.classList.add('qa-open')
      })
    })

    document.body.classList.add('qa-no-scroll')
    this.querySelector('.qa-body').scrollTop = 0
    this.querySelector('.qa-details').scrollTop = 0
    this.querySelector('.qa-close').focus({ preventScroll: true })
  }

  close () {
    this.classList.remove('qa-open')
    document.body.classList.remove('qa-no-scroll')

    setTimeout(() => {
      if (!this.classList.contains('qa-open')) {
        this.backdrop.hidden = true
        this.panel.hidden = true
      }
    }, 300)
  }

  renderHeader () {
    this.querySelector('[data-qa-title]').textContent = this.product.title
  }

  renderGallery () {
    const images = this.product.images || []

    this.gallery.innerHTML = images.map((src, i) => `
      <div class="qa-gallery-slide">
        <img src="${src}" alt="" loading="${i === 0 ? 'eager' : 'lazy'}">
      </div>
    `).join('')

    this.dots.innerHTML = images.length > 1
      ? images.map((_, i) => `<span class="qa-dot ${i === 0 ? 'active' : ''}"></span>`).join('')
      : ''

    if (this.thumbs) {
      if (images.length > 1) {
        this.thumbs.innerHTML = images.map((src, i) => `
          <button class="qa-thumb ${i === 0 ? 'active' : ''}" type="button" data-thumb-index="${i}" aria-label="Image ${i + 1}">
            <img src="${src}" alt="" loading="lazy">
          </button>
        `).join('')
        this.thumbs.hidden = false
        this.thumbs.querySelectorAll('.qa-thumb').forEach(btn => {
          btn.addEventListener('click', () => {
            const index = Number(btn.dataset.thumbIndex)
            this.gallery.scrollTo({ left: index * this.gallery.clientWidth, behavior: 'smooth' })
          })
        })
      } else {
        this.thumbs.innerHTML = ''
        this.thumbs.hidden = true
      }
    }

    this.gallery.scrollLeft = 0

    const ratingEl = this.querySelector('[data-qa-rating]')
    if (this.product.rating) {
      const count = this.product.rating_count
        ? ` <span class="qa-rating-divider">|</span> ${this.product.rating_count}`
        : ''
      ratingEl.innerHTML = `
        <svg class="rating-star" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg>
        ${Number(this.product.rating).toFixed(1)}${count}
      `
      ratingEl.hidden = false
    } else {
      ratingEl.hidden = true
    }
  }

  onGalleryScroll () {
    const slides = this.gallery.querySelectorAll('.qa-gallery-slide')
    if (!slides.length) return

    const index = Math.round(this.gallery.scrollLeft / this.gallery.clientWidth)
    this.dots.querySelectorAll('.qa-dot').forEach((dot, i) => {
      dot.classList.toggle('active', i === index)
    })
    this.thumbs?.querySelectorAll('.qa-thumb').forEach((thumb, i) => {
      thumb.classList.toggle('active', i === index)
      if (i === index) {
        thumb.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' })
      }
    })
  }

  renderInfo () {
    const couponEl = this.querySelector('[data-qa-coupon]')
    const couponText = this.dataset.couponText

    if (couponText && this.product.compare_at_price > this.product.price) {
      couponEl.textContent = couponText
      couponEl.hidden = false
    } else if (this.product.best_price > 0 && this.product.best_price_code) {
      couponEl.textContent = 'Use code ' + this.product.best_price_code
      couponEl.hidden = false
    } else if (couponText && this.product.best_price > 0) {
      couponEl.textContent = couponText
      couponEl.hidden = false
    } else {
      couponEl.hidden = true
    }

    this.querySelector('[data-qa-product-title]').textContent = this.product.title
    this.querySelector('[data-qa-product-link]').setAttribute('href', this.product.url)

    this.updatePrices(this.product.price, this.product.compare_at_price)
  }

  updatePrices (price, compareAtPrice) {
    this.querySelector('[data-qa-price]').textContent = this.formatMoney(price)

    const mrpEl = this.querySelector('[data-qa-mrp]')
    if (compareAtPrice > price) {
      mrpEl.innerHTML = `${this.dataset.mrpLabel} <s>${this.formatMoney(compareAtPrice)}</s>`
      mrpEl.hidden = false
    } else {
      mrpEl.hidden = true
    }
  }

  renderOptions () {
    const options = (this.product.options || []).filter(() => !this.product.has_only_default_variant)

    this.optionsWrapper.innerHTML = options.map(option => `
      <div class="qa-option-group" data-option-position="${option.position}" data-option-name="${this.escapeHtml(option.name)}">
        <h3 class="qa-option-title">${this.dataset.textSelect} ${this.escapeHtml(option.name)}</h3>
        <p class="qa-option-error" hidden>${this.dataset.textSelectError} ${this.escapeHtml(option.name.toLowerCase())}</p>
        <div class="qa-pills">
          ${option.values.map(value => `
            <button class="qa-pill" type="button" data-option-value="${this.escapeHtml(value)}">
              ${this.escapeHtml(value)}
            </button>
          `).join('')}
        </div>
      </div>
    `).join('')

    this.optionsWrapper.querySelectorAll('.qa-pill').forEach(pill => {
      pill.addEventListener('click', () => this.onSelectOption(pill))
    })

    this.refreshPillAvailability()
  }

  onSelectOption (pill) {
    const group = pill.closest('.qa-option-group')
    const position = Number(group.dataset.optionPosition)
    const value = pill.dataset.optionValue

    if (this.selectedOptions[position] === value) {
      delete this.selectedOptions[position]
      pill.classList.remove('selected')
    } else {
      this.selectedOptions[position] = value
      group.querySelectorAll('.qa-pill').forEach(p => p.classList.remove('selected'))
      pill.classList.add('selected')
    }

    group.classList.remove('qa-error')
    group.querySelector('.qa-option-error').hidden = true

    this.refreshPillAvailability()
    this.updateAtcState()

    const variant = this.findSelectedVariant()
    if (variant) {
      this.updatePrices(variant.price, variant.compare_at_price || this.product.compare_at_price)
    }
  }

  variantMatches (variant, constraints) {
    return Object.entries(constraints).every(([position, value]) =>
      variant[`option${position}`] === value
    )
  }

  refreshPillAvailability () {
    this.optionsWrapper.querySelectorAll('.qa-option-group').forEach(group => {
      const position = Number(group.dataset.optionPosition)

      group.querySelectorAll('.qa-pill').forEach(pill => {
        const constraints = { ...this.selectedOptions, [position]: pill.dataset.optionValue }
        const hasAvailable = this.product.variants.some(v =>
          v.available && this.variantMatches(v, constraints)
        )
        pill.classList.toggle('sold-out', !hasAvailable)
      })
    })
  }

  findSelectedVariant () {
    const optionCount = (this.product.options || []).length
    if (Object.keys(this.selectedOptions).length !== optionCount) return null

    return this.product.variants.find(v => this.variantMatches(v, this.selectedOptions)) || null
  }

  updateAtcState () {
    const anyAvailable = this.product.variants.some(v => v.available)

    if (!anyAvailable) {
      this.atcBtn.disabled = true
      this.atcBtn.textContent = this.dataset.textSoldOut
      return
    }

    this.atcBtn.textContent = this.dataset.textAtc

    const variant = this.findSelectedVariant()
    this.atcBtn.disabled = Boolean(variant && !variant.available)
  }

  async onAddToCart () {
    // Highlight any option group that has no selection yet
    const groups = [...this.optionsWrapper.querySelectorAll('.qa-option-group')]
    const missing = groups.filter(group =>
      !(Number(group.dataset.optionPosition) in this.selectedOptions)
    )

    if (missing.length) {
      missing.forEach(group => {
        group.classList.add('qa-error')
        group.querySelector('.qa-option-error').hidden = false
      })
      missing[0].scrollIntoView({ behavior: 'smooth', block: 'center' })
      return
    }

    let variant

    if (this.product.has_only_default_variant) {
      variant = this.product.variants[0]
    } else {
      variant = this.findSelectedVariant()
    }

    if (!variant || !variant.available) return

    this.atcBtn.classList.add('loading')
    this.atcBtn.disabled = true

    await this.addVariantToCart(variant.id)

    this.atcBtn.classList.remove('loading')
    this.atcBtn.disabled = false

    // The theme's cart-container opens the cart drawer after adding
    this.close()
  }

  renderSizeGuide () {
    const el = this.querySelector('[data-qa-size-guide]')
    const url = this.dataset.sizeGuideUrl

    if (url && url.length && !this.product.has_only_default_variant) {
      el.setAttribute('href', url)
      el.hidden = false
    } else {
      el.hidden = true
    }
  }

  renderWishlist () {
    if (this.dataset.wishlistEnabled !== 'true' || !document.querySelector('wishlist-container')) {
      this.wishlistBtn.hidden = true
      return
    }

    this.wishlistBtn.hidden = false
    this.syncWishlistState()
  }

  syncWishlistState () {
    const container = document.querySelector('wishlist-container')
    if (!container) return

    const isWishlisted = container.wishlist.some(item => item.url === this.product.url)
    this.wishlistBtn.classList.toggle('active', isWishlisted)
    this.wishlistBtn.setAttribute('aria-pressed', isWishlisted ? 'true' : 'false')
  }

  async onToggleWishlist () {
    const container = document.querySelector('wishlist-container')
    if (!container || !this.product) return

    await container.setProduct(this.product.url)
    this.syncWishlistState()

    // Keep the hearts on the product cards in sync
    document.querySelectorAll('wishlist-button').forEach(btn => {
      if (typeof btn.adjustBtn === 'function') btn.adjustBtn()
    })
  }

  escapeHtml (value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;')
  }
}
customElements.define('quick-add-drawer', QuickAddDrawer)
